import { z } from "zod";
import { SELECTED_WORKBOOK_MAX_FIELDS, selectedWorkbookAnchorSchema, selectedWorkbookFieldIdSchema, selectedWorkbookPreparationSchema, selectedWorkbookRejectionCodeSchema, } from "./selected-workbook-preparation.js";
export const SELECTED_WORKBOOK_DEPENDENCIES_MAX_BYTES = 256_000;
export const SELECTED_WORKBOOK_DEPENDENCIES_MAX_FIELD_REFERENCES = 64;
export const SELECTED_WORKBOOK_DEPENDENCIES_MAX_REFERENCES = 512;
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const address = selectedWorkbookAnchorSchema.shape.address;
const count = z.number().int().nonnegative().max(SELECTED_WORKBOOK_MAX_FIELDS);
export const selectedWorkbookUnsupportedReferenceReasonSchema = z.enum([
    "UNKNOWN_TOKEN",
    "FUNCTION",
    "RANGE",
    "DYNAMIC",
    "STRUCTURED",
]);
export const selectedWorkbookDependencyDiagnosticSchema = z.enum([
    "DYNAMIC",
    "FUNCTION",
    "MALFORMED_SYNTAX",
    "RANGE",
    "SOURCE_REJECTED",
    "STRUCTURED",
    "UNKNOWN_TOKEN",
]);
/** Lexical occurrences only. A local address never implies that its cell was opened. */
export const selectedWorkbookDependencyReferenceSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("SELECTED_FIELD"),
        address,
        targetFieldId: selectedWorkbookFieldIdSchema,
    })
        .strict(),
    z.object({ kind: z.literal("LOCAL_ANCHOR"), address }).strict(),
    z.object({ kind: z.literal("LOCAL_OUTSIDE_PROFILE"), address }).strict(),
    z.object({ kind: z.literal("CROSS_SHEET"), referenceSha256: sha256 }).strict(),
    z.object({ kind: z.literal("EXTERNAL"), referenceSha256: sha256 }).strict(),
    z
        .object({
        kind: z.literal("UNSUPPORTED"),
        syntaxSha256: sha256,
        reason: selectedWorkbookUnsupportedReferenceReasonSchema,
    })
        .strict(),
]);
/** A rejected observation is not assumed to contain a formula. Unknown expressions stay null. */
export const selectedWorkbookDependencySourceSchema = z.discriminatedUnion("state", [
    z
        .object({ state: z.literal("NOT_A_FORMULA"), formulaSha256: z.null(), rejectionCode: z.null() })
        .strict(),
    z
        .object({
        state: z.literal("FORMULA_UNQUALIFIED"),
        formulaSha256: sha256,
        rejectionCode: z.null(),
    })
        .strict(),
    z
        .object({
        state: z.literal("REJECTED"),
        formulaSha256: sha256.nullable(),
        rejectionCode: selectedWorkbookRejectionCodeSchema,
    })
        .strict(),
]);
const fieldSchema = z
    .object({
    fieldId: selectedWorkbookFieldIdSchema,
    source: selectedWorkbookDependencySourceSchema,
    references: z
        .array(selectedWorkbookDependencyReferenceSchema)
        .max(SELECTED_WORKBOOK_DEPENDENCIES_MAX_FIELD_REFERENCES),
    diagnostics: z.array(selectedWorkbookDependencyDiagnosticSchema).max(7),
})
    .strict();
/**
 * An offline companion to the unchanged preparation manifest. It describes direct syntax
 * without evaluating formulas, using caches, following dependencies or qualifying a mapping.
 */
export const selectedWorkbookDependenciesSchema = z
    .object({
    schemaVersion: z.literal("selected-workbook-dependencies.v1"),
    producerVersion: z.literal("selected-workbook-dependencies-v1"),
    purpose: z.literal("DEPENDENCY_REVIEW_ONLY"),
    state: z.literal("BLOCKED"),
    executable: z.literal(false),
    completeness: z.literal("PARTIAL"),
    formulasEvaluated: z.literal(0),
    cacheInputsUsed: z.literal(0),
    externalDependenciesOpened: z.literal(0),
    referenceTraversal: z.literal("NONE"),
    preparationSha256: sha256,
    preparation: selectedWorkbookPreparationSchema,
    fields: z.array(fieldSchema).min(1).max(SELECTED_WORKBOOK_MAX_FIELDS),
    counts: z
        .object({
        selectedFields: count.positive(),
        formulaFields: count,
        rejectedFields: count,
        nonFormulaFields: count,
        references: z
            .number()
            .int()
            .nonnegative()
            .max(SELECTED_WORKBOOK_DEPENDENCIES_MAX_REFERENCES),
        unsupportedFields: count,
    })
        .strict(),
})
    .strict()
    .superRefine((report, context) => {
    const invalid = (message) => context.addIssue({ code: "custom", message });
    const observations = report.preparation.fields;
    const values = new Map(report.preparation.profile.fields.map((field) => [field.valueAddress, field.fieldId]));
    const anchors = new Set(report.preparation.profile.anchors.map((anchor) => anchor.address));
    if (report.fields.length !== observations.length)
        invalid("DEPENDENCY_FIELDS_INCOMPLETE");
    const expectedCounts = {
        selectedFields: report.fields.length,
        formulaFields: 0,
        rejectedFields: 0,
        nonFormulaFields: 0,
        references: 0,
        unsupportedFields: 0,
    };
    for (const [index, field] of report.fields.entries()) {
        const original = observations[index];
        if (field.fieldId !== original?.fieldId)
            invalid("DEPENDENCY_FIELD_ORDER_INVALID");
        const observation = original?.observation;
        const source = field.source;
        if (observation?.kind === "FORMULA_UNQUALIFIED") {
            if (source.state !== "FORMULA_UNQUALIFIED" ||
                source.formulaSha256 !== observation.formulaSha256)
                invalid("DEPENDENCY_FORMULA_SOURCE_INVALID");
        }
        else if (observation?.kind === "REJECTED") {
            if (source.state !== "REJECTED" || source.rejectionCode !== observation.code)
                invalid("DEPENDENCY_REJECTION_SOURCE_INVALID");
        }
        else if (source.state !== "NOT_A_FORMULA")
            invalid("DEPENDENCY_LITERAL_SOURCE_INVALID");
        if (source.state === "NOT_A_FORMULA") {
            expectedCounts.nonFormulaFields += 1;
            if (field.references.length || field.diagnostics.length)
                invalid("DEPENDENCY_LITERAL_HAS_REFERENCES");
        }
        if (source.formulaSha256 !== null)
            expectedCounts.formulaFields += 1;
        if (source.state === "REJECTED") {
            expectedCounts.rejectedFields += 1;
            if (!field.diagnostics.includes("SOURCE_REJECTED"))
                invalid("DEPENDENCY_REJECTION_DIAGNOSTIC_MISSING");
            if (source.rejectionCode !== "FORMULA_UNSUPPORTED" && source.formulaSha256 !== null)
                invalid("DEPENDENCY_HAZARDOUS_FORMULA_EXPOSED");
            if (source.formulaSha256 === null &&
                (field.references.length ||
                    field.diagnostics.length !== 1 ||
                    field.diagnostics[0] !== "SOURCE_REJECTED"))
                invalid("DEPENDENCY_UNKNOWN_EXPRESSION_ANALYZED");
        }
        else if (field.diagnostics.includes("SOURCE_REJECTED"))
            invalid("DEPENDENCY_REJECTION_DIAGNOSTIC_INVALID");
        const sortedDiagnostics = [...new Set(field.diagnostics)].sort();
        if (JSON.stringify(sortedDiagnostics) !== JSON.stringify(field.diagnostics))
            invalid("DEPENDENCY_DIAGNOSTICS_ORDER_INVALID");
        if (field.diagnostics.length ||
            field.references.some((reference) => reference.kind === "UNSUPPORTED"))
            expectedCounts.unsupportedFields += 1;
        expectedCounts.references += field.references.length;
        for (const reference of field.references) {
            if (reference.kind === "SELECTED_FIELD") {
                if (values.get(reference.address) !== reference.targetFieldId)
                    invalid("DEPENDENCY_SELECTED_TARGET_INVALID");
            }
            else if (reference.kind === "LOCAL_ANCHOR") {
                if (!anchors.has(reference.address))
                    invalid("DEPENDENCY_ANCHOR_TARGET_INVALID");
            }
            else if (reference.kind === "LOCAL_OUTSIDE_PROFILE") {
                if (values.has(reference.address) || anchors.has(reference.address))
                    invalid("DEPENDENCY_OUTSIDE_TARGET_INVALID");
            }
            else if (reference.kind === "UNSUPPORTED" &&
                !field.diagnostics.includes(reference.reason))
                invalid("DEPENDENCY_UNSUPPORTED_DIAGNOSTIC_MISSING");
        }
        if (field.diagnostics.includes("MALFORMED_SYNTAX")) {
            const reference = field.references[0];
            if (field.references.length !== 1 ||
                reference?.kind !== "UNSUPPORTED" ||
                reference.reason !== "UNKNOWN_TOKEN" ||
                reference.syntaxSha256 !== source.formulaSha256)
                invalid("DEPENDENCY_MALFORMED_REFERENCES_INVALID");
        }
    }
    if (Object.entries(expectedCounts).some(([key, value]) => report.counts[key] !== value) ||
        expectedCounts.references > SELECTED_WORKBOOK_DEPENDENCIES_MAX_REFERENCES)
        invalid("DEPENDENCY_COUNTS_INVALID");
    if (new TextEncoder().encode(JSON.stringify(report)).length >
        SELECTED_WORKBOOK_DEPENDENCIES_MAX_BYTES)
        invalid("DEPENDENCY_REPORT_TOO_LARGE");
});
//# sourceMappingURL=selected-workbook-dependencies.js.map